import React, { useState, useEffect } from 'react'
import { categoriesAPI } from '../services/api'
import { Category } from '../types'
import './Categories.css'

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  })

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      setIsLoading(true)
      const data = await categoriesAPI.getAll()
      setCategories(data.categories || [])
    } catch (error) {
      console.error('Failed to load categories:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingCategory(null)
    setFormData({ name: '', description: '' })
    setShowForm(true)
  }

  const handleEdit = (category: Category) => {
    setEditingCategory(category)
    setFormData({
      name: category.name,
      description: category.description || ''
    })
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa danh mục này?')) {
      return
    }

    try {
      await categoriesAPI.delete(id)
      loadCategories()
    } catch (error) {
      console.error('Failed to delete category:', error)
      alert('Không thể xóa danh mục')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name) {
      alert('Vui lòng nhập tên danh mục')
      return
    }

    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id.toString(), formData)
        alert('Cập nhật danh mục thành công!')
      } else {
        await categoriesAPI.create(formData)
        alert('Tạo danh mục thành công!')
      }
      
      setShowForm(false)
      setEditingCategory(null)
      loadCategories()
    } catch (error) {
      console.error('Failed to save category:', error)
      alert('Không thể lưu danh mục')
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingCategory(null)
  }

  if (isLoading) {
    return <div className="loading">Đang tải...</div>
  }

  return (
    <div className="categories-page">
      <div className="page-header">
        <h2>Quản lý danh mục</h2>
        <button className="btn btn-primary" onClick={handleCreate}>+ Thêm danh mục mới</button>
      </div>

      <div className="categories-table">
        <table>
          <thead>
            <tr>
              <th>Tên danh mục</th>
              <th>Slug</th>
              <th>Mô tả</th>
              <th>Ngày tạo</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {categories.length > 0 ? categories.map(category => (
              <tr key={category.id}>
                <td>
                  <strong>{category.name}</strong>
                </td>
                <td>
                  <code>{category.slug}</code>
                </td>
                <td>{category.description || 'Không có mô tả'}</td>
                <td>{new Date(category.created_at).toLocaleDateString('vi-VN')}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn btn-sm btn-edit" 
                      onClick={() => handleEdit(category)}
                    >
                      Sửa
                    </button>
                    <button 
                      className="btn btn-sm btn-delete" 
                      onClick={() => handleDelete(category.id.toString())}
                    >
                      Xóa
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={5} className="no-data">
                  Không có danh mục nào
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingCategory ? 'Sửa danh mục' : 'Thêm danh mục mới'}</h3>
              <button className="modal-close" onClick={handleCancel}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="category-form">
              <div className="form-group">
                <label htmlFor="name">Tên danh mục *</label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Nhập tên danh mục"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Mô tả</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Nhập mô tả danh mục"
                  rows={4}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Hủy
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Cập nhật' : 'Tạo mới'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Categories